/**
 * Workspace — iter-121.28 cosmetic redesign.
 *
 * Same business logic as the iter-121.22 baseline (workspace/asset/share APIs,
 * 30 s soft poll, drag-to-reorder localStorage, delete/leave/share flows) —
 * just a polished, premium 3-column layout per the user's design comp:
 *
 *   ┌─────────────┬──────────────────────────────┬──────────────────┐
 *   │  HEADER     │                              │                  │
 *   │  Workspace  │   Search assets…   ▦ ▤       │  Workspace      │
 *   │  Search…    │   All  Dashboards  Datasets  │  details        │
 *   │  ─────────  │   Newest first ▾             │   (folder)       │
 *   │  workspace  │                              │   Owner: …       │
 *   │  list rows  │   asset cards                │   Created: …     │
 *   │             │                              │   Sharing        │
 *   │             │                              │   Tip            │
 *   └─────────────┴──────────────────────────────┴──────────────────┘
 *
 * All workspaces render as a flat list (Personal / Shared / External
 * badges) — no expandable groups. Selection works the same way it does
 * on the legacy 3-pane resizable shell, so test IDs (`workspace-tree-
 * item-<id>`, `workspace-share-button`, `workspace-delete-button`, etc.)
 * still match. AssetGrid + ShareDialog + CreateWorkspaceDialog +
 * GuestInvitePanel + StatRow + timeAgo are reused verbatim — this page
 * file only owns the layout shell.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useEntitlements } from "@/lib/use-entitlements";
import { useSettings } from "@/lib/settings-context";
import { useConfirm } from "@/lib/confirm-context";
import {
    Folder, Search, FolderPlus,
    Share2, Users as UsersIcon, Lock, Globe2, X, Trash2,
    MoreHorizontal, ChevronDown, LayoutGrid, List as ListIcon,
    User as UserIcon, Clock, FileText, Tag, Lightbulb,
    LayoutDashboard, Database, Images, Sparkles, Pin, PinOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { AssetGrid, ShareSummary } from "@/components/workspace/AssetGrid";
import CreateWorkspaceDialog from "@/components/workspace/CreateWorkspaceDialog";
import ShareDialog from "@/components/workspace/ShareDialog";
import BreadcrumbPortal from "@/components/layout/BreadcrumbPortal";

import { GuestInvitePanel, StatRow, DetailsAccordion, timeAgo, formatDate } from "@/components/workspace/DetailsAside";

const ASSET_TYPE_ICON = {
    dashboard: LayoutDashboard,
    dataset:   Database,
    gallery:   Images,
};

/* -------------------------------------------------------------------------- */
/*  WorkspaceListRow — single workspace entry in the left sidebar             */
/* -------------------------------------------------------------------------- */
function WorkspaceListRow({
    workspace, isActive, isOwner, assetCountForActive,
    onSelect, onShare, onDelete, onLeave,
    isPinned, onTogglePin,
    dragProps,
}) {
     const { user } = useAuth();
    const w = workspace;
    const tone =
      w.virtual ? "shared"
        : w.source === "external_shared" ? "external"
        : w.kind === "personal" ? "personal"
        : "shared";

    const badgeText = tone === "personal" ? "Personal"
        : tone === "external" ? "Shared"
        : "Shared";

    const badgeClass = tone === "personal"
        ? "bg-primary/10 text-primary border-primary/20"
        : tone === "external"
        ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20"
        : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20";

    // Asset count is only resolved for the active workspace (the parent
    // owns the assets fetch). Otherwise we show the share-mode label.
    const subtitleText = isActive
        ? `${assetCountForActive ?? 0} Asset${assetCountForActive === 1 ? "" : "s"}`
        : w.source === "external_shared"
            ? (w.owner_org_name || "External org")
            : w.share_mode === "tenant" ? "Org-wide"
            : w.share_mode === "members" ? `${(w.shared_with || []).length} member${(w.shared_with || []).length === 1 ? "" : "s"}`
            : "Private";

    return (
        <div
            {...(dragProps || {})}
            className={`group relative flex items-stretch rounded-md transition-colors ${
                isActive
                    ? "bg-primary/5 border border-primary/20"
                    : "border border-transparent hover:bg-secondary/40 hover:border-border"
            }`}
            data-testid={`workspace-tree-item-${w.id}`}>
            {isActive && (
                <span className="absolute left-0 top-2 bottom-2 w-0.5 bg-primary rounded-full" aria-hidden />
            )}
            <button
                type="button"
                onClick={() => onSelect(w)}
                className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2.5 text-left"
                data-testid={`workspace-row-${w.id}`}>
                <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${
                    isActive ? "bg-primary/10" : "bg-secondary"
                }`}>
                    <Folder className={`h-4 w-4 ${isActive ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium truncate" title={w.name}>
                            {w.name}
                        </span>
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-sm border shrink-0 ${badgeClass}`}>
                            {badgeText}
                        </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                        {subtitleText}
                    </div>
                </div>
            </button>
            <div className="flex items-center my-auto mr-1.5 shrink-0">
                <button
                    className={`flex items-center justify-center h-8 w-8 rounded-md transition-colors ${
                        isPinned
                            ? "text-primary bg-primary/10 hover:bg-primary/20"
                            : "text-muted-foreground/50 hover:text-foreground hover:bg-background"
                    }`}
                    onClick={(e) => { e.stopPropagation(); onTogglePin(w); }}
                    title={isPinned ? "Unpin Target Workspace" : "Pin as Target Workspace"}
                    data-testid={`workspace-row-pin-${w.id}`}>
                    <Pin className={`h-4 w-4 ${isPinned ? "fill-primary rotate-45" : ""}`} />
                </button>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-background transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                            title="Workspace actions"
                            data-testid={`workspace-row-menu-${w.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                        </button>
                    </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {w.name}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSelect(w); }}
                        data-testid={`workspace-row-open-${w.id}`}>
                        <Folder className="h-3.5 w-3.5 mr-2" /> Open
                    </DropdownMenuItem>
                    {onShare && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onShare(w); }}
                            data-testid={`workspace-row-share-${w.id}`}>
                            <Share2 className="h-3.5 w-3.5 mr-2" /> Share
                        </DropdownMenuItem>
                    )}
                    {onDelete && isOwner && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(w); }}
                                className="text-destructive focus:text-destructive"
                                data-testid={`workspace-row-delete-${w.id}`}>
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete…
                            </DropdownMenuItem>
                        </>
                    )}
                    {onLeave && !isOwner && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onLeave(w); }}
                                data-testid={`workspace-row-leave-${w.id}`}>
                                <X className="h-3.5 w-3.5 mr-2" /> Leave…
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  FilterChip — pill button for the asset-type filter row                    */
/* -------------------------------------------------------------------------- */
function FilterChip({ label, count, active, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
            }`}>
            <span>{label}</span>
            <span className={`tabular-nums ${active ? "opacity-90" : "opacity-70"}`}>
                ({count})
            </span>
        </button>
    );
}

/* -------------------------------------------------------------------------- */
/*  DetailRow — key/value row in the right details panel                      */
/* -------------------------------------------------------------------------- */
function DetailRow({ icon: Icon, label, children, testId }) {
    return (
        <div className="flex items-center gap-3 py-2.5" data-testid={testId}>
            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" strokeWidth={1.75} />
            <span className="text-xs text-muted-foreground w-20 shrink-0">{label}</span>
            <span className="text-xs font-medium text-foreground flex-1 min-w-0 truncate">{children}</span>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Workspace — main page                                                     */
/* -------------------------------------------------------------------------- */
export default function Workspace() {
    const { user } = useAuth();
    const { getAccessLevel } = useSettings();
    const workspaceLevel = getAccessLevel("workspace");
    const confirm = useConfirm();
    const [workspaces, setWorkspaces] = useState([]);
    const [users, setUsers] = useState([]);
    const [selected, setSelected] = useState(null);
    const [assets, setAssets] = useState({ dashboards: [], datasets: [], gallery: [] });
    const [tab, setTab] = useState("all");
    const [q, setQ] = useState("");                // asset search (center)
    const [wsQuery, setWsQuery] = useState("");    // workspace search (left)
    const [sortMode, setSortMode] = useState("newest");
    const [createOpen, setCreateOpen] = useState(false);
    // iter-2026 — plan gate: Standard can use the two default workspaces (My +
    // Shared) but cannot create additional ones. `multi_workspace` feature
    // drives whether the New workspace CTA is active or locked (upgrade badge).
    const [canCreateWs, setCanCreateWs] = useState(true);
    const { openUpgrade } = useEntitlements();
    const [shareTarget, setShareTarget] = useState(null);
    const [viewMode, setViewMode] = useState("tiles");
    const [pinnedWsId, setPinnedWsId] = useState(() => {
        try { return localStorage.getItem("analyzio_pinned_workspace_id"); } catch { return null; }
    });
    const nav = useNavigate();

    const handleTogglePin = useCallback(() => {
        if (!selected) return;
        if (pinnedWsId === selected.id) {
            try { localStorage.removeItem("analyzio_pinned_workspace_id"); } catch {}
            setPinnedWsId(null);
            toast.success("Unpinned target workspace");
        } else {
            try { localStorage.setItem("analyzio_pinned_workspace_id", selected.id); } catch {}
            setPinnedWsId(selected.id);
            toast.success(`Pinned "${selected.name}" as the target workspace`);
        }
    }, [selected, pinnedWsId]);

    /* ───────── data loading (unchanged from iter-121.22 baseline) ───────── */
    const load = useCallback(async () => {
        const tid = user?.tenant_id;
        const [w, u] = await Promise.all([
            api.get("/workspaces"),
            api.get(tid ? `/users?tenant_id=${encodeURIComponent(tid)}` : "/users")
                .catch(() => ({ data: [] })),
        ]);
        setWorkspaces(w.data);
        setUsers(u.data);
    }, [user?.tenant_id]);
    useEffect(() => { load(); }, [load]);

    // Resolve the plan's `multi_workspace` entitlement so the New workspace
    // CTA can lock (with an Upgrade badge) on Standard. SA always allowed.
    useEffect(() => {
        let alive = true;
        (async () => {
            if (user?.role === "super_admin") { setCanCreateWs(true); return; }
            try {
                const r = await api.get("/billing/usage-summary");
                if (alive) setCanCreateWs(!!(r.data?.features?.multi_workspace));
            } catch { /* leave default */ }
        })();
        return () => { alive = false; };
    }, [user?.role]);

    // Soft 30 s poll for newly-shared workspaces (parity with iter-121.22).
    useEffect(() => {
        let timer = null;
        const tick = async () => {
            try {
                const w = await api.get("/workspaces");
                setWorkspaces((prev) => {
                    const next = w.data || [];
                    if (prev.length !== next.length) return next;
                    const prevSig = prev.map((x) => `${x.id}:${x.updated_at || ""}:${x.share_mode || ""}`).join("|");
                    const nextSig = next.map((x) => `${x.id}:${x.updated_at || ""}:${x.share_mode || ""}`).join("|");
                    return prevSig === nextSig ? prev : next;
                });
            } catch { /* swallow */ }
        };
        const arm = () => { if (timer) clearInterval(timer); timer = setInterval(tick, 30000); };
        const onVisibility = () => {
            if (document.hidden) {
                if (timer) { clearInterval(timer); timer = null; }
            } else { tick(); arm(); }
        };
        arm();
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            if (timer) clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    const loadAssets = async (ws) => {
        if (!ws) return;
        const r = await api.get(`/workspaces/${ws.id}/assets`);
        setAssets(r.data);
    };

    useEffect(() => {
        if (selected) loadAssets(selected);
        if (!selected && workspaces.length) {
            // Priority 1: The user's explicitly pinned target workspace (if they have one)
            const pinned = pinnedWsId ? workspaces.find((w) => w.id === pinnedWsId) : null;
            
            // Priority 2: Fall back to the first personal workspace the user owns
            const first = pinned || workspaces.find((w) => w.kind === "personal" && w.created_by === user?.id) || workspaces[0];
            
            if (first) setSelected(first);
        }
        // eslint-disable-next-line
    }, [selected, workspaces, pinnedWsId]);

    const userById = (id) => users.find((u) => u.id === id);
    const createdByName = (w) => {
        if (!w) return "—";
        if (w.created_by === user?.id) return `${user.name} (you)`;
        return userById(w.created_by)?.name || "Unknown";
    };

    /* ───────── workspace classification (flat list) ───────── */
    const visibleWorkspaces = useMemo(() => {
        const term = wsQuery.trim().toLowerCase();
        const list = !term ? workspaces
            : workspaces.filter((w) => (w.name || "").toLowerCase().includes(term));
        // Drag-reorder persisted by id (preserved from iter-121.22).
        try {
            const orderKey = `analyzio_ws_order_${user?.id}_flat`;
            const orderIds = JSON.parse(localStorage.getItem(orderKey) || "[]");
            if (orderIds.length) {
                const index = Object.fromEntries(orderIds.map((id, i) => [id, i]));
                return [...list].sort((a, b) => (index[a.id] ?? 9999) - (index[b.id] ?? 9999));
            }
        } catch { /* swallow */ }
        return list;
    }, [workspaces, wsQuery, user?.id]);

    /* ───────── drag-to-reorder (flat list, owner-only) ───────── */
    const [dragId, setDragId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const orderKey = `analyzio_ws_order_${user?.id}_flat`;
    const onDragStart = (id) => (e) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
    const onDragOver = (id) => (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverId(id); };
    const onDragLeave = () => setDragOverId(null);
    const onDrop = (targetId) => (e) => {
        e.preventDefault();
        if (!dragId || dragId === targetId) { setDragId(null); setDragOverId(null); return; }
        const list = [...visibleWorkspaces];
        const from = list.findIndex((w) => w.id === dragId);
        const to = list.findIndex((w) => w.id === targetId);
        if (from < 0 || to < 0) return;
        const [moved] = list.splice(from, 1);
        list.splice(to, 0, moved);
        try { localStorage.setItem(orderKey, JSON.stringify(list.map((w) => w.id))); } catch { /* swallow */ }
        setDragId(null); setDragOverId(null);
        // Force re-render of visibleWorkspaces by bumping a state.
        setWorkspaces((prev) => [...prev]);
    };

    /* ───────── asset helpers ───────── */
    const allAssets = [
        ...assets.dashboards.map((d) => ({ ...d, _kind: "dashboard", _type: "dashboard" })),
        ...assets.datasets.map((d)   => ({ ...d, _kind: "dataset",   _type: "dataset"   })),
        ...assets.gallery.map((d)    => ({ ...d, _kind: "gallery",   _type: "gallery"   })),
    ];
    const totalAssetCount = allAssets.length;
    const filter = (arr) => q.trim() ? arr.filter((x) => (x.name || "").toLowerCase().includes(q.toLowerCase())) : arr;

    // Sort by "newest first" / "oldest first" / "name" (a→z) when applicable.
    const sortItems = (items) => {
        const out = [...items];
        if (sortMode === "newest" || sortMode === "oldest") {
            out.sort((a, b) => {
                const at = new Date(a.updated_at || a.created_at || 0).getTime();
                const bt = new Date(b.updated_at || b.created_at || 0).getTime();
                return sortMode === "newest" ? bt - at : at - bt;
            });
        } else if (sortMode === "name") {
            out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        }
        return out;
    };

    const activeAssetList = useMemo(() => {
        let src = allAssets;
        if (tab === "dashboards") src = assets.dashboards.map((d) => ({ ...d, _type: "dashboard" }));
        else if (tab === "datasets") src = assets.datasets.map((d) => ({ ...d, _type: "dataset" }));
        else if (tab === "gallery")  src = assets.gallery.map((d)  => ({ ...d, _type: "gallery"  }));
        else if (tab === "others")   src = [];
        return sortItems(filter(src));
        // eslint-disable-next-line
    }, [tab, q, sortMode, assets]);

    /* ───────── role-aware permissions (unchanged) ───────── */
    // const canManageShare = selected && !selected.virtual
    const hasWrite = workspaceLevel === "W" || workspaceLevel === "A" || user?.role === "super_admin";
    const canManageShare = hasWrite && selected && !selected.virtual
        && (selected.created_by === user?.id || ["super_admin", "org_admin"].includes(user?.role));
    const isOwner = selected && !selected.virtual && selected.created_by === user?.id;
    // const canDelete = isOwner && selected.kind !== "personal";
    const canDelete = hasWrite && isOwner && selected.kind !== "personal";
    const canLeave = selected && !isOwner && selected.kind !== "personal";

    /* ───────── delete / leave (unchanged behavior) ───────── */
    const onDelete = async (ws) => {
        const target = ws || selected;
        if (!target) return;
        const ok = await confirm({
            title: `Delete "${target.name}"?`,
            description: "This permanently removes the workspace and all of its dashboards, datasets, and gallery items. This cannot be undone.",
            confirmLabel: "Delete workspace",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/workspaces/${target.id}`);
            toast.success("Workspace deleted");
            setWorkspaces((prev) => prev.filter((w) => w.id !== target.id));
            if (selected?.id === target.id) setSelected(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const onLeave = async (ws) => {
        const target = ws || selected;
        if (!target) return;
        const ok = await confirm({
            title: `Leave "${target.name}"?`,
            description: "You'll lose access, but the workspace and all of its assets stay intact for other members.",
            confirmLabel: "Leave workspace",
            variant: "warning",
        });
        if (!ok) return;
        try {
            await api.post(`/workspaces/${target.id}/leave`);
            toast.success("You've left this workspace");
            setWorkspaces((prev) => prev.filter((w) => w.id !== target.id));
            if (selected?.id === target.id) setSelected(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Leave failed");
        }
    };

    const onWorkspaceCreated = (ws) => {
        setWorkspaces([...workspaces, ws]);
        setSelected(ws);
    };

    const onShareUpdated = async (updated) => {
        if (shareTarget?.type === "workspace") {
            const next = workspaces.map((w) => w.id === updated.id ? { ...w, ...updated } : w);
            setWorkspaces(next);
            setSelected(updated);
        } else {
            loadAssets(selected);
        }
        setShareTarget(null);
    };

    /* ───────── right-panel "Sharing" pill line ───────── */
    const renderSharingLine = (w) => {
        if (!w) return null;
        const mode = w.share_mode || (w.kind === "shared" ? "tenant" : "private");
        if (mode === "private") return (
            <div className="flex items-center gap-2 text-sm text-foreground" data-testid="ws-sharing-line">
                <Lock className="h-3.5 w-3.5 text-primary" />
                <span>Private — only you</span>
            </div>
        );
        if (mode === "tenant") return (
            <div className="flex items-center gap-2 text-sm text-foreground" data-testid="ws-sharing-line">
                <Globe2 className="h-3.5 w-3.5 text-primary" />
                <span>Everyone in your organisation</span>
            </div>
        );
        const count = (w.shared_with || []).length;
        return (
            <div className="flex items-center gap-2 text-sm text-foreground" data-testid="ws-sharing-line">
                <UsersIcon className="h-3.5 w-3.5 text-primary" />
                <span>{count} member{count === 1 ? "" : "s"}</span>
            </div>
        );
    };

    /* ───────── workspace type label for the right details panel ───────── */
    const workspaceTypeLabel = (w) => {
        if (!w) return "—";
        if (w.source === "external_shared") return "External workspace";
        if (w.virtual) return "Shared workspace";
        if (w.kind === "shared") return "Shared workspace";
        return "Personal workspace";
    };

    /* ─────────────────────────── render ─────────────────────────── */
    // New-workspace CTA — active when the plan allows it, otherwise a locked
    // button with an "Upgrade" badge (parity with locked Enterprise features).
    const NewWorkspaceCTA = ({ size, variant, className = "", testId }) => {
        if (canCreateWs) {
            return (
                <Button
                    size={size} variant={variant}
                    className={className}
                    onClick={() => setCreateOpen(true)}
                    data-testid={testId}>
                    <FolderPlus className={size === "sm" ? "h-3.5 w-3.5 mr-1.5" : "h-4 w-4"} />
                    New workspace
                </Button>
            );
        }
        return (
            <Button
                size={size} variant="outline"
                className={`${className} opacity-90`}
                title="Creating additional workspaces requires an Enterprise plan."
                onClick={() => openUpgrade()}
                data-testid={`${testId}-locked`}>
                <Lock className={size === "sm" ? "h-3.5 w-3.5 mr-1.5" : "h-4 w-4 mr-1.5"} />
                New workspace
                <Badge variant="secondary" className="ml-2 text-[9px] font-mono uppercase tracking-wider" data-testid="workspace-upgrade-badge">
                    Upgrade
                </Badge>
            </Button>
        );
    };
    return (
        <div className="fadein min-h-[calc(100vh-64px)] bg-background" data-testid="workspace-page">
            {/* Top action bar — only the New workspace CTA lives here so the
                rest of the page can dedicate every pixel to the three columns. */}
            {/* iter-2026 — New workspace CTA lifted onto the breadcrumb row
                (via BreadcrumbPortal) so it no longer needs its own line. */}
            <BreadcrumbPortal>
                {hasWrite && (
                    <NewWorkspaceCTA
                        className="h-8 rounded-md gap-1.5"
                        testId="new-workspace-button" />
                )}
            </BreadcrumbPortal>

            <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)_340px] gap-0 px-6 pt-4 pb-4">
                {/* ────────────────── LEFT — workspace list ────────────────── */}
                <aside className="lg:pr-5" data-testid="workspace-tree">
                    <div className="mb-5">
                        <h1 className="text-2xl font-semibold tracking-tight text-foreground" data-testid="workspace-title">
                            Workspace
                        </h1>
                        <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                            Organize your work by creating workspaces and managing assets.
                        </p>
                    </div>
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <input
                            type="search"
                            value={wsQuery}
                            onChange={(e) => setWsQuery(e.target.value)}
                            placeholder="Search workspaces…"
                            className="w-full h-10 pl-9 pr-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-shadow"
                            data-testid="workspace-search"
                        />
                    </div>
                    <div className="space-y-1" data-testid="workspace-list">
                        {visibleWorkspaces.length === 0 ? (
                            <div className="border border-dashed border-border rounded-md py-10 px-4 text-center" data-testid="workspace-list-empty">
                                <Folder className="h-7 w-7 mx-auto mb-2 text-muted-foreground/40" strokeWidth={1.5} />
                                <div className="text-sm text-foreground/80">No workspaces yet</div>
                                <div className="text-[11px] text-muted-foreground mt-1">
                                    Create your first workspace to organise dashboards, datasets, and gallery items.
                                </div>
                                {/* <Button
                                    size="sm"
                                    variant="outline"
                                    className="mt-4 rounded-md"
                                    onClick={() => setCreateOpen(true)}
                                    data-testid="workspace-list-empty-cta">
                                    <FolderPlus className="h-3.5 w-3.5 mr-1.5" />
                                    New workspace
                                </Button> */}
                                 {hasWrite && (
                                    <NewWorkspaceCTA
                                        size="sm" variant="outline"
                                        className="mt-4 rounded-md"
                                        testId="workspace-list-empty-cta" />
                                )}
                            </div>
                        ) : (
                            visibleWorkspaces.map((w) => {
                                const ownerForRow = w.created_by === user?.id;
                                const dragProps = (hasWrite && ownerForRow) ? {
                                    draggable: true,
                                    onDragStart: onDragStart(w.id),
                                    onDragOver: onDragOver(w.id),
                                    onDragLeave,
                                    onDrop: onDrop(w.id),
                                    onDragEnd: () => { setDragId(null); setDragOverId(null); },
                                    style: dragId === w.id ? { opacity: 0.4 } : undefined,
                                } : {};
                                const isActive = selected?.id === w.id;
                                const isDropTarget = dragOverId === w.id && dragId && dragId !== w.id;
                                return (
                                    <div
                                        key={w.id}
                                        className={isDropTarget ? "border-t-2 border-t-primary rounded-none" : ""}>
                                        <WorkspaceListRow
                                            workspace={w}
                                            isActive={isActive}
                                            isOwner={ownerForRow}
                                            assetCountForActive={isActive ? totalAssetCount : null}
                                            onSelect={setSelected}
                                            onShare={canManageShare && isActive ? (target) => setShareTarget({ type: "workspace", item: target }) : null}
                                            onDelete={ownerForRow && w.kind !== "personal" ? onDelete : null}
                                            onLeave={!ownerForRow && w.kind !== "personal" ? onLeave : null}
                                            isPinned={pinnedWsId === w.id}
                                            onTogglePin={(workspace) => {
                                                setSelected(workspace);
                                                // We must wrap handleTogglePin or use a small inline logic since handleTogglePin uses `selected` from state.
                                                // Actually, it's safer to just replicate the toggle logic here for the specific workspace.
                                                if (pinnedWsId === workspace.id) {
                                                    try { localStorage.removeItem("analyzio_pinned_workspace_id"); } catch {}
                                                    setPinnedWsId(null);
                                                    toast.success("Unpinned target workspace");
                                                } else {
                                                    try { localStorage.setItem("analyzio_pinned_workspace_id", workspace.id); } catch {}
                                                    setPinnedWsId(workspace.id);
                                                    toast.success(`Pinned "${workspace.name}" as the target workspace`);
                                                }
                                            }}
                                            dragProps={dragProps}
                                        />
                                    </div>
                                );
                            })
                        )}
                    </div>
                </aside>

                {/* ────────────────── CENTER — assets ────────────────── */}
                <section className="lg:px-5 lg:border-x lg:border-border" data-testid="workspace-center">
                    {/* Search + view-mode toggle */}
                    <div className="flex items-center gap-3 mb-5">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <input
                                type="search"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={`Search assets${selected ? ` in ${selected.name}` : ""}…`}
                                className="w-full h-10 pl-9 pr-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-shadow"
                                data-testid="workspace-filter-input"
                            />
                        </div>
                        <div className="flex items-center border border-border rounded-md p-0.5 bg-background">
                            <button
                                type="button"
                                onClick={() => setViewMode("tiles")}
                                className={`h-9 w-9 inline-flex items-center justify-center rounded-sm transition-colors ${
                                    viewMode === "tiles" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                                }`}
                                title="Grid view"
                                data-testid="workspace-view-tiles">
                                <LayoutGrid className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode("details")}
                                className={`h-9 w-9 inline-flex items-center justify-center rounded-sm transition-colors ${
                                    viewMode === "details" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
                                }`}
                                title="List view"
                                data-testid="workspace-view-details">
                                <ListIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Filter chips */}
                    <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="workspace-tabs">
                        <FilterChip label="All"        count={totalAssetCount}            active={tab === "all"}        onClick={() => setTab("all")}        testId="tab-all" />
                        <FilterChip label="Dashboards" count={assets.dashboards.length}   active={tab === "dashboards"} onClick={() => setTab("dashboards")} testId="tab-dashboards" />
                        <FilterChip label="Datasets"   count={assets.datasets.length}     active={tab === "datasets"}   onClick={() => setTab("datasets")}   testId="tab-datasets" />
                        <FilterChip label="Gallery"    count={assets.gallery.length}      active={tab === "gallery"}    onClick={() => setTab("gallery")}    testId="tab-gallery" />
                        <FilterChip label="Others"     count={0}                          active={tab === "others"}     onClick={() => setTab("others")}     testId="tab-others" />
                    </div>

                    {/* Sort dropdown */}
                    <div className="flex items-center justify-between mb-3" data-testid="workspace-toolbar">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-secondary rounded-md transition-colors"
                                    data-testid="workspace-sort-toggle">
                                    <span>
                                        {sortMode === "newest" ? "Newest first"
                                            : sortMode === "oldest" ? "Oldest first"
                                            : "Name (A → Z)"}
                                    </span>
                                    <ChevronDown className="h-3.5 w-3.5" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-44">
                                <DropdownMenuItem onClick={() => setSortMode("newest")} data-testid="workspace-sort-newest">Newest first</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setSortMode("oldest")} data-testid="workspace-sort-oldest">Oldest first</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setSortMode("name")}   data-testid="workspace-sort-name">Name (A → Z)</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <div className="text-[11px] font-mono text-muted-foreground">
                            {activeAssetList.length} item{activeAssetList.length === 1 ? "" : "s"}
                        </div>
                    </div>

                    {/* Asset grid / list */}
                    {selected ? (
                        tab === "others" ? (
                            <div className="border border-dashed border-border rounded-md py-16 px-4 text-center" data-testid="assets-others-empty">
                                <Sparkles className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" strokeWidth={1.5} />
                                <div className="text-sm text-foreground/80">Nothing here yet</div>
                                <div className="text-[11px] text-muted-foreground mt-1 max-w-xs mx-auto">
                                    "Others" is reserved for future asset types — saved queries, alerts, and more.
                                </div>
                            </div>
                        ) : activeAssetList.length === 0 ? (
                            <div className="border border-dashed border-border rounded-md py-16 px-4 text-center" data-testid="assets-empty">
                                <Folder className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" strokeWidth={1.5} />
                                <div className="text-sm text-foreground/80">
                                    {q.trim() ? "No assets match your search" : "No assets in this workspace yet"}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-1">
                                    {q.trim()
                                        ? "Try a different keyword or clear the search."
                                        : "Add dashboards, datasets, or gallery items to this workspace to see them here."}
                                </div>
                            </div>
                        ) : (
                            <AssetGrid
                                items={activeAssetList}
                                viewMode={viewMode}
                                onOpen={(item) => {
                                    if (item._type === "dashboard") nav(`/dashboards/${item.id}`);
                                    else if (item._type === "dataset") nav(`/data?dataset=${item.id}`);
                                    else nav(`/gallery?preview=${item.id}`);
                                }}
                                onShare={canManageShare ? (item) => setShareTarget({ type: item._type, item, workspace: selected }) : null}
                            />
                        )
                    ) : (
                        <div className="border border-dashed border-border rounded-md py-16 px-4 text-center" data-testid="workspace-no-selection">
                            <Folder className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" strokeWidth={1.5} />
                            <div className="text-sm text-foreground/80">Select a workspace to view its assets</div>
                        </div>
                    )}
                </section>

                {/* ────────────────── RIGHT — workspace details ────────────────── */}
                <aside className="lg:pl-5 mt-6 lg:mt-0" data-testid="workspace-metadata-panel">
                    {!selected ? (
                        <div className="border border-border rounded-md py-10 px-4 text-center text-xs text-muted-foreground" data-testid="ws-details-empty">
                            Pick a workspace on the left to see its details.
                        </div>
                    ) : (
                        <div className="space-y-5" data-testid="workspace-details-content">
                            {/* Header row + Share */}
                            <div className="flex items-center justify-between">
                                <h2 className="text-sm font-semibold text-foreground">Workspace details</h2>
                                <div className="flex items-center gap-1">
                                    {canManageShare && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-8 rounded-md gap-1.5"
                                            onClick={() => setShareTarget({ type: "workspace", item: selected })}
                                            data-testid="workspace-share-button">
                                            <Share2 className="h-3.5 w-3.5" />
                                            Share
                                        </Button>
                                    )}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <button
                                                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                                title="More actions"
                                                data-testid="workspace-details-menu">
                                                <MoreHorizontal className="h-4 w-4" />
                                            </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-48">
                                            <DropdownMenuItem onClick={() => selected && loadAssets(selected)} data-testid="ws-details-refresh">
                                                <Clock className="h-3.5 w-3.5 mr-2" /> Refresh assets
                                            </DropdownMenuItem>
                                            {canDelete && (
                                                <>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem
                                                        onClick={() => onDelete()}
                                                        className="text-destructive focus:text-destructive"
                                                        data-testid="workspace-delete-button">
                                                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete workspace…
                                                    </DropdownMenuItem>
                                                </>
                                            )}
                                            {canLeave && (
                                                <>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem
                                                        onClick={() => onLeave()}
                                                        data-testid="workspace-leave-button">
                                                        <X className="h-3.5 w-3.5 mr-2" /> Leave workspace…
                                                    </DropdownMenuItem>
                                                </>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>

                            {/* Identity card: prominent folder + name + badge */}
                            <div className="rounded-lg bg-secondary/30 border border-border p-5 text-center relative overflow-hidden" data-testid="ws-identity-card">
                                {pinnedWsId === selected.id && (
                                    <div className="absolute top-0 right-0 p-2 text-primary" title="Target Workspace">
                                        <Pin className="h-4 w-4 fill-primary rotate-45" />
                                    </div>
                                )}
                                <div className="mx-auto h-20 w-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-3">
                                    <Folder className="h-10 w-10 text-primary" strokeWidth={1.5} />
                                </div>
                                <div className="text-base font-semibold text-foreground" data-testid="ws-details-name" title={selected.name}>
                                    {selected.name}
                                </div>
                                <div className="mt-2 inline-flex">
                                    <Badge
                                        variant="outline"
                                        className={`text-[10px] font-medium ${
                                           selected.virtual
                                                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20"
                                                : selected.source === "external_shared"
                                                    ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20"
                                                    : selected.kind === "personal"
                                                        ? "bg-primary/10 text-primary border-primary/20"
                                                        : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20"
                                        }`}
                                        data-testid="ws-details-badge">
                                           {selected.source === "external_shared" ? "Shared" : selected.kind === "personal" ? "Personal" : "Shared"}
                                    </Badge>
                                </div>
                                <div className="mt-4 flex justify-center">
                                    <Button
                                        variant={pinnedWsId === selected.id ? "secondary" : "outline"}
                                        size="sm"
                                        className="w-full text-xs"
                                        onClick={handleTogglePin}>
                                        {pinnedWsId === selected.id ? (
                                            <><PinOff className="h-3.5 w-3.5 mr-2" /> Unpin Target</>
                                        ) : (
                                            <><Pin className="h-3.5 w-3.5 mr-2" /> Pin as Target</>
                                        )}
                                    </Button>
                                </div>
                            </div>

                            {/* Description (optional) */}
                            {selected.description && !selected.virtual && (
                                <div className="text-[12px] text-muted-foreground leading-relaxed" data-testid="workspace-description">
                                    {selected.description}
                                </div>
                            )}

                            {/* Metadata rows */}
                            <div className="divide-y divide-border" data-testid="ws-details-rows">
                                <DetailRow icon={UserIcon} label="Owner" testId="quick-stat-owner">
                                    {selected.virtual ? (selected.owner_org_name || "External org") : createdByName(selected)}
                                </DetailRow>
                                <DetailRow icon={Clock} label="Created" testId="quick-stat-modified">
                                    <span title={selected.created_at ? new Date(selected.created_at).toLocaleString() : ""}>
                                        {selected.created_at ? formatDate(selected.created_at) : "—"}
                                    </span>
                                </DetailRow>
                                <DetailRow icon={FileText} label="Assets" testId="quick-stat-assets">
                                    {totalAssetCount}
                                </DetailRow>
                                <DetailRow icon={Tag} label="Type" testId="quick-stat-kind">
                                    {workspaceTypeLabel(selected)}
                                </DetailRow>
                            </div>

                            {/* External-org callout */}
                            {selected.source === "external_shared" && !selected.virtual && (
                                <div className="border border-amber-500/30 bg-amber-500/5 rounded-md p-3 text-[11px] leading-relaxed">
                                    <Globe2 className="inline h-3 w-3 mr-1 mb-0.5 text-amber-700 dark:text-amber-300" />
                                    Hosted by <b>{selected.owner_org_name}</b>. You have{" "}
                                    <b>{selected.guest_access === "A" ? "admin" : selected.guest_access === "W" ? "write" : "read"}</b>{" "}
                                    access.
                                </div>
                            )}

                            {/* Sharing section */}
                            {!selected.virtual && (
                                <div className="pt-2" data-testid="ws-sharing-block">
                                    <div className="text-xs font-semibold text-foreground mb-2">Sharing</div>
                                    {renderSharingLine(selected)}
                                    {(selected.shared_with || []).length > 0 && (
                                        <div className="mt-2">
                                            <ShareSummary item={selected} users={users} currentUser={user} />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Guest invite (owners of shared workspaces) */}
                            {canDelete && selected && selected.source !== "external_shared" && (
                                <div className="pt-3 border-t border-border">
                                    <GuestInvitePanel workspace={selected} />
                                </div>
                            )}

                            {/* Show-more disclosure */}
                            <DetailsAccordion selected={selected} createdByName={createdByName} userById={userById} />

                            {/* Tip card */}
                            <div className="rounded-md border border-primary/20 bg-primary/5 p-3" data-testid="ws-tip-card">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                    <Lightbulb className="h-3.5 w-3.5 text-primary" />
                                    <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">Tip</span>
                                </div>
                                <p className="text-[11px] text-muted-foreground leading-relaxed">
                                    Use workspaces to keep related dashboards, datasets, and files organised.
                                </p>
                            </div>
                        </div>
                    )}
                </aside>
            </div>

            {/* Dialogs (unchanged) */}
            <CreateWorkspaceDialog
                open={createOpen} onOpenChange={setCreateOpen}
                users={users} currentUser={user}
                onCreated={onWorkspaceCreated}
            />
            <ShareDialog
                target={shareTarget} onOpenChange={(o) => !o && setShareTarget(null)}
                users={users} currentUser={user}
                onSaved={onShareUpdated}
            />
        </div>
    );
}
